import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import StaffingLevels from './StaffingLevels';
import DuplicateShiftModal from './DuplicateShiftModal';
import AIProactiveInsights from './AIProactiveInsights';
import AIScheduleBuilder from './AIScheduleBuilder';
import AIChatBox from './AIChatBox';
import CoverageComparison from './CoverageComparison';
import {
  AlertTriangle,
  Clock,
  Users,
  Phone,
  CalendarX,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Shield,
  TrendingDown,
  BarChart3,
  UserMinus,
  Copy,
  Timer,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CACHE_KEY = 'schedule_insights_cache';

const ScheduleInsights = () => {
  const { user } = useAuth();
  const [chatMessage, setChatMessage] = useState(null);
  const [insights, setInsights] = useState(() => {
    // Load from sessionStorage for instant rendering
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(!insights);
  const [refreshing, setRefreshing] = useState(false);
  const [weeksThreshold, setWeeksThreshold] = useState(3);
  const [expandedSections, setExpandedSections] = useState({
    alerts: true,
    expiring: true,
    unscheduled: true,
    employeeGaps: true,
    regularGaps: true,
    oncallGaps: true,
    duplicateShifts: true,
    excessiveHours: true,
    coverage: true
  });
  // Pagination state for large lists
  const [dupPage, setDupPage] = useState(1);
  const [excessPage, setExcessPage] = useState(1);
  const [gapPage, setGapPage] = useState(1);
  const PAGE_SIZE = 20;
  // Duplicate shift modal state
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);

  const fetchInsights = async (forceRefresh = false) => {
    if (insights) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/schedule-insights`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { weeks_threshold: weeksThreshold, force_refresh: forceRefresh }
      });
      setInsights(response.data);
      // Reset pagination on new data
      setDupPage(1);
      setExcessPage(1);
      setGapPage(1);
      // Cache for instant next visit
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(response.data)); } catch {}
    } catch (error) {
      if (!insights) toast.error('Failed to load schedule insights');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [weeksThreshold]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const formatDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric'
    });
  };

  const SeverityBadge = ({ severity }) => {
    const styles = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-amber-100 text-amber-800 border-amber-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${styles[severity] || styles.low}`}>
        {severity.toUpperCase()}
      </span>
    );
  };

  const CollapsibleSection = ({ id, title, icon: Icon, iconColor, count, children }) => (
    <Card>
      <button
        onClick={() => toggleSection(id)}
        className="w-full"
        data-testid={`section-toggle-${id}`}
      >
        <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${iconColor}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="text-left">
                <CardTitle className="text-base">{title}</CardTitle>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {count !== undefined && (
                <span className={`text-sm font-semibold px-2.5 py-1 rounded-full ${
                  count > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                }`}>
                  {count}
                </span>
              )}
              {expandedSections[id] ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </div>
          </div>
        </CardHeader>
      </button>
      {expandedSections[id] && (
        <CardContent className="pt-0">
          {children}
        </CardContent>
      )}
    </Card>
  );

  if (loading && !insights) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!insights) return null;

  const { alerts, expiring_schedules, unscheduled_employees, gap_days, oncall_gap_days, employee_gaps, shrinkage_summary, duplicate_shifts, excessive_hours, daily_summary, baselines } = insights;

  return (
    <div className="space-y-6" data-testid="schedule-insights-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-500" />
            Schedule Insights
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            AI-powered analysis, schedule building, and coverage alerts
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Lookahead:</span>
            <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
              {[2, 3, 4, 6, 8].map(w => (
                <button
                  key={w}
                  onClick={() => setWeeksThreshold(w)}
                  data-testid={`threshold-${w}w`}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                    weeksThreshold === w
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {w}w
                </button>
              ))}
            </div>
          </div>
          <Button
            onClick={() => fetchInsights(true)}
            variant="outline"
            size="sm"
            disabled={loading || refreshing}
            data-testid="refresh-insights"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* AI Proactive Insights */}
      <AIProactiveInsights onChatAction={setChatMessage} />

      {/* Coverage vs Requirements */}
      <CoverageComparison />

      {/* AI Schedule Builder */}
      <AIScheduleBuilder />

      {/* Refreshing indicator */}
      {refreshing && (
        <div className="flex items-center space-x-2 text-sm text-blue-600 bg-blue-50 px-4 py-2 rounded-lg" data-testid="refreshing-banner">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Updating insights data...</span>
        </div>
      )}

      {/* Alerts Summary Cards */}
      {alerts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {alerts.map((alert, i) => (
            <Card key={i} className={`border-l-4 ${
              alert.severity === 'high' ? 'border-l-red-500' : 'border-l-amber-500'
            }`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <SeverityBadge severity={alert.severity} />
                    </div>
                    <p className="text-sm text-gray-700 mt-2">{alert.message}</p>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{alert.count}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center space-x-3">
              <Shield className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">All Clear</p>
                <p className="text-sm text-green-600">No scheduling issues detected for the next {weeksThreshold} weeks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expiring Schedules */}
      <CollapsibleSection
        id="expiring"
        title="Expiring Schedules"
        icon={Clock}
        iconColor="bg-orange-100 text-orange-600"
        count={expiring_schedules.length}
      >
        {expiring_schedules.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">All employee schedules extend beyond {weeksThreshold} weeks</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expiring-schedules-table">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">Employee</th>
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">Role</th>
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">Last Scheduled</th>
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">Time Left</th>
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {expiring_schedules.map((emp) => (
                  <tr key={emp.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2.5 px-3 font-medium text-gray-900">{emp.name}</td>
                    <td className="py-2.5 px-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{emp.role}</span>
                    </td>
                    <td className="py-2.5 px-3 text-gray-700">{formatDate(emp.last_scheduled_date)}</td>
                    <td className="py-2.5 px-3">
                      <span className={`font-medium ${
                        emp.days_remaining <= 7 ? 'text-red-600' : 
                        emp.days_remaining <= 14 ? 'text-amber-600' : 'text-gray-700'
                      }`}>
                        {emp.days_remaining}d ({emp.weeks_remaining}w)
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      {emp.days_remaining <= 7 ? (
                        <Badge variant="destructive" className="text-xs">Urgent</Badge>
                      ) : emp.days_remaining <= 14 ? (
                        <Badge className="bg-amber-100 text-amber-800 text-xs border-amber-200">Soon</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Approaching</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleSection>

      {/* Unscheduled Employees */}
      <CollapsibleSection
        id="unscheduled"
        title="Unscheduled Employees"
        icon={Users}
        iconColor="bg-red-100 text-red-600"
        count={unscheduled_employees.length}
      >
        {unscheduled_employees.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">All active employees have future shifts scheduled</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {unscheduled_employees.map((emp) => (
              <div key={emp.user_id} className="flex items-center space-x-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center text-red-700 font-medium text-sm">
                  {emp.name.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{emp.name}</p>
                  <p className="text-xs text-gray-500">{emp.role} {emp.work_location === 'remote' ? '(remote)' : ''}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* Staffing Baselines Summary */}
      {baselines && Object.keys(baselines).length > 0 && (
        <Card data-testid="baselines-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center space-x-2">
              <TrendingDown className="h-4 w-4 text-blue-600" />
              <span>Normal Staffing Levels</span>
              <span className="text-xs font-normal text-gray-500">(based on past 4 weeks)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="baselines-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">Day</th>
                    <th className="text-center py-2 px-3 text-gray-600 font-medium">Regular Total</th>
                    <th className="text-center py-2 px-3 text-gray-600 font-medium">On-Call Total</th>
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">Regular by Role</th>
                    <th className="text-left py-2 px-3 text-gray-600 font-medium">On-Call by Role</th>
                  </tr>
                </thead>
                <tbody>
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => {
                    const b = baselines[day] || {};
                    return (
                      <tr key={day} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium text-gray-900">{day}</td>
                        <td className="py-2 px-3 text-center">
                          <span className="font-semibold text-blue-700">{b.regular_total || 0}</span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className="font-semibold text-orange-700">{b.oncall_total || 0}</span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(b.regular_by_role || {}).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
                              <span key={role} className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                                {role.replace('_', ' ')}: {count}
                              </span>
                            ))}
                            {Object.keys(b.regular_by_role || {}).length === 0 && (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(b.oncall_by_role || {}).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
                              <span key={role} className="text-xs px-1.5 py-0.5 rounded bg-orange-50 text-orange-700">
                                {role.replace('_', ' ')}: {count}
                              </span>
                            ))}
                            {Object.keys(b.oncall_by_role || {}).length === 0 && (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Individual Schedule Gaps - Per-employee missing shifts & shrinkage */}
      <CollapsibleSection
        id="employeeGaps"
        title="Individual Schedule Gaps"
        icon={UserMinus}
        iconColor="bg-purple-100 text-purple-600"
        count={employee_gaps ? employee_gaps.filter(e => e.missing_count > 0).length : 0}
      >
        {!employee_gaps || employee_gaps.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">All employees are fully scheduled based on their normal work patterns</p>
        ) : (
          <div className="space-y-4">
            {/* Shrinkage summary banner */}
            {(() => {
              const totalPto = (employee_gaps || []).reduce((sum, e) => sum + (e.pto_count || 0), 0);
              const totalExpected = (employee_gaps || []).reduce((sum, e) => sum + (e.total_expected || 0), 0);
              const overallShrinkage = totalExpected > 0 ? ((totalPto / totalExpected) * 100).toFixed(1) : 0;
              if (totalPto > 0) {
                return (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-100" data-testid="shrinkage-banner">
                    <div className="flex items-center space-x-2">
                      <TrendingDown className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">
                        PTO Shrinkage: {totalPto} day(s) across {employee_gaps.filter(e => e.pto_count > 0).length} employee(s)
                      </span>
                    </div>
                    <span className="text-sm font-bold text-amber-700">{overallShrinkage}%</span>
                  </div>
                );
              }
              return null;
            })()}

            {/* Group by role */}
            {(() => {
              const roleGroups = {};
              (employee_gaps || []).forEach(emp => {
                const role = emp.role || 'unknown';
                if (!roleGroups[role]) roleGroups[role] = [];
                roleGroups[role].push(emp);
              });
              const sortedRoles = Object.entries(roleGroups).sort((a, b) => {
                const aMissing = a[1].reduce((s, e) => s + e.missing_count, 0);
                const bMissing = b[1].reduce((s, e) => s + e.missing_count, 0);
                return bMissing - aMissing;
              });
              return sortedRoles.map(([role, emps]) => {
                const totalMissing = emps.reduce((s, e) => s + e.missing_count, 0);
                const totalPto = emps.reduce((s, e) => s + (e.pto_count || 0), 0);
                const totalExpected = emps.reduce((s, e) => s + e.total_expected, 0);
                const roleShrinkage = totalExpected > 0 ? ((totalPto / totalExpected) * 100).toFixed(1) : 0;
                const roleBaseline = baselines ? Object.values(baselines).reduce((sum, b) =>
                  sum + (b.regular_by_role?.[role] || 0), 0) / 5 : 0;

                return (
                  <div key={role} className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`role-group-${role}`}>
                    <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-gray-900 capitalize">{role.replace('_', ' ')}</span>
                        <span className="text-xs text-gray-500">
                          ({emps.length} employee{emps.length > 1 ? 's' : ''}{roleBaseline > 0 ? ` · avg ${Math.round(roleBaseline)}/day baseline` : ''})
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-xs">
                        {totalMissing > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold">
                            {totalMissing} missing
                          </span>
                        )}
                        {totalPto > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
                            {totalPto} PTO ({roleShrinkage}% shrink)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {emps.map((emp) => (
                        <div key={emp.user_id} className={`px-4 py-2.5 ${
                          emp.missing_count > 0 ? 'bg-purple-50/40' : 'bg-amber-50/30'
                        }`} data-testid={`employee-gap-${emp.user_id}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-medium text-gray-900">{emp.name}</span>
                            <div className="flex items-center space-x-2 text-xs">
                              {emp.missing_count > 0 && (
                                <span className="font-semibold text-purple-700">{emp.missing_count} missing</span>
                              )}
                              {emp.pto_count > 0 && (
                                <span className="font-semibold text-amber-600">{emp.pto_count} PTO</span>
                              )}
                              <span className="text-gray-400">/ {emp.total_expected} expected</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {emp.missing_dates?.map((md) => (
                              <span key={md.date} className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                                {formatDate(md.date)}
                              </span>
                            ))}
                            {emp.pto_dates?.map((md) => (
                              <span key={md.date} className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                                {formatDate(md.date)} (PTO)
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </CollapsibleSection>

      {/* Regular Shift Gaps */}
      <CollapsibleSection
        id="regularGaps"
        title="Regular Shift Gaps"
        icon={CalendarX}
        iconColor="bg-red-100 text-red-600"
        count={gap_days.length}
      >
        {gap_days.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">Every workday meets normal regular staffing levels</p>
        ) : (
          <div className="space-y-3">
            {gap_days.map((gap) => {
              const isHigh = gap.severity === 'high';
              return (
                <div key={gap.date} className={`p-3 rounded-lg ${
                  isHigh ? 'bg-red-50 border border-red-100' : 'bg-amber-50 border border-amber-100'
                }`} data-testid={`gap-${gap.date}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <CalendarX className={`h-4 w-4 ${isHigh ? 'text-red-500' : 'text-amber-500'}`} />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{formatDate(gap.date)}</span>
                        <span className="text-xs text-gray-500 ml-2">{gap.day_of_week}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 text-sm">
                      <span className={`font-semibold ${isHigh ? 'text-red-600' : 'text-amber-600'}`}>
                        {gap.reason}
                      </span>
                      <span className="text-xs text-gray-500">
                        (normal: {gap.expected_regular || 0})
                      </span>
                    </div>
                  </div>
                  {/* Per-role shortage breakdown */}
                  {gap.shortages && gap.shortages.length > 0 && (
                    <div className="flex flex-wrap gap-2 ml-7">
                      {gap.shortages.map((s) => (
                        <span key={s.role} className={`text-xs px-2 py-1 rounded-full font-medium ${
                          s.actual === 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {s.role.replace('_', ' ')}: {s.actual}/{s.expected}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>

      {/* On-Call Coverage Gaps */}
      <CollapsibleSection
        id="oncallGaps"
        title="On-Call Coverage Gaps"
        icon={Phone}
        iconColor="bg-amber-100 text-amber-600"
        count={oncall_gap_days.length}
      >
        {oncall_gap_days.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">Every workday meets normal on-call staffing levels</p>
        ) : (
          <div className="space-y-3">
            {oncall_gap_days.map((gap) => {
              const isHigh = gap.severity === 'high';
              return (
                <div key={gap.date} className={`p-3 rounded-lg ${
                  isHigh ? 'bg-red-50 border border-red-100' : 'bg-amber-50 border border-amber-100'
                }`} data-testid={`oncall-gap-${gap.date}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <Phone className={`h-4 w-4 ${isHigh ? 'text-red-500' : 'text-amber-500'}`} />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{formatDate(gap.date)}</span>
                        <span className="text-xs text-gray-500 ml-2">{gap.day_of_week}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 text-sm">
                      <span className={`font-semibold ${isHigh ? 'text-red-600' : 'text-amber-600'}`}>
                        {gap.reason}
                      </span>
                      <span className="text-xs text-gray-500">
                        (normal: {gap.expected_oncall || 0})
                      </span>
                    </div>
                  </div>
                  {/* Per-role shortage breakdown */}
                  {gap.shortages && gap.shortages.length > 0 && (
                    <div className="flex flex-wrap gap-2 ml-7">
                      {gap.shortages.map((s) => (
                        <span key={s.role} className={`text-xs px-2 py-1 rounded-full font-medium ${
                          s.actual === 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {s.role.replace('_', ' ')}: {s.actual}/{s.expected}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CollapsibleSection>

      {/* Duplicate / Overlapping Shifts */}
      <CollapsibleSection
        id="duplicateShifts"
        title="Duplicate / Overlapping Shifts"
        icon={Copy}
        iconColor="bg-red-100 text-red-600"
        count={duplicate_shifts ? duplicate_shifts.length : 0}
      >
        {!duplicate_shifts || duplicate_shifts.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">No duplicate or overlapping shifts found</p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 mb-1">Click a row to resolve the conflict</p>
            {duplicate_shifts.slice(0, dupPage * PAGE_SIZE).map((dup, idx) => (
              <div
                key={`${dup.user_id}-${dup.date}-${idx}`}
                onClick={() => setSelectedDuplicate(dup)}
                className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md hover:scale-[1.005] ${
                  dup.has_overlap ? 'bg-red-50 border-red-100 hover:border-red-300' : 'bg-amber-50 border-amber-100 hover:border-amber-300'
                }`}
                data-testid={`duplicate-${dup.user_id}-${dup.date}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <Copy className={`h-4 w-4 ${dup.has_overlap ? 'text-red-500' : 'text-amber-500'}`} />
                    <div>
                      <span className="text-sm font-semibold text-gray-900">{dup.name}</span>
                      <span className="text-xs px-1.5 py-0.5 ml-2 rounded bg-gray-100 text-gray-600">{dup.role?.replace('_', ' ')}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="text-gray-500">{formatDate(dup.date)}</span>
                    {dup.has_overlap ? (
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">Overlapping</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{dup.shift_count} shifts</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 ml-7">
                  {dup.shifts?.map((s, i) => (
                    <span key={i} className={`text-xs px-2 py-1 rounded-full font-medium ${
                      s.shift_type === 'on_call' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {s.shift_type?.replace('_', ' ')} {s.start_time}-{s.end_time}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {duplicate_shifts.length > dupPage * PAGE_SIZE && (
              <button onClick={() => setDupPage(p => p + 1)} className="w-full py-2 text-sm text-blue-600 hover:text-blue-800 font-medium bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors" data-testid="load-more-duplicates">
                Show more ({duplicate_shifts.length - dupPage * PAGE_SIZE} remaining)
              </button>
            )}
          </div>
        )}
      </CollapsibleSection>

      {/* Excessive Hours */}
      <CollapsibleSection
        id="excessiveHours"
        title="Excessive Hours"
        icon={Timer}
        iconColor="bg-orange-100 text-orange-600"
        count={excessive_hours ? excessive_hours.length : 0}
      >
        {!excessive_hours || excessive_hours.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">No employees exceeding their normal regular hours</p>
        ) : (
          <div className="space-y-2">
            {(() => {
              // Group by role, then paginate total entries
              const roleGroups = {};
              (excessive_hours || []).forEach(e => {
                const role = e.role || 'unknown';
                if (!roleGroups[role]) roleGroups[role] = [];
                roleGroups[role].push(e);
              });
              const sorted = Object.entries(roleGroups).sort((a, b) => {
                const aMax = Math.max(...a[1].map(e => e.overage_pct));
                const bMax = Math.max(...b[1].map(e => e.overage_pct));
                return bMax - aMax;
              });
              // Show up to excessPage * PAGE_SIZE total entries across all roles
              let shown = 0;
              const limit = excessPage * PAGE_SIZE;
              const totalEntries = excessive_hours.length;
              return (<>
                {sorted.map(([role, entries]) => {
                  if (shown >= limit) return null;
                  const entriesToShow = entries.slice(0, limit - shown);
                  shown += entriesToShow.length;
                  return (
                    <div key={role} className="rounded-lg border border-gray-200 overflow-hidden" data-testid={`excessive-role-${role}`}>
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                        <span className="text-sm font-semibold text-gray-900 capitalize">{role.replace('_', ' ')}</span>
                        <span className="text-xs text-gray-500 ml-2">({entries.length} instance{entries.length > 1 ? 's' : ''})</span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {entriesToShow.map((e, i) => (
                          <div key={`${e.user_id}-${e.week}-${i}`} className="px-4 py-2.5 bg-orange-50/40" data-testid={`excessive-${e.user_id}-${e.week}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-900">{e.name}</span>
                              <div className="flex items-center space-x-2 text-xs">
                                <span className="text-gray-500">{e.week}</span>
                                <span className="font-semibold text-orange-700">{e.scheduled_hours}h</span>
                                <span className="text-gray-400">vs</span>
                                <span className="text-gray-600">{e.normal_hours}h normal</span>
                                <span className={`px-2 py-0.5 rounded-full font-semibold ${
                                  e.overage_pct > 25 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                                }`}>
                                  +{e.overage}h ({e.overage_pct}%)
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              {e.days_scheduled} days scheduled in this week
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {totalEntries > limit && (
                  <button onClick={() => setExcessPage(p => p + 1)} className="w-full py-2 text-sm text-blue-600 hover:text-blue-800 font-medium bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors" data-testid="load-more-excessive">
                    Show more ({totalEntries - limit} remaining)
                  </button>
                )}
              </>);
            })()}
          </div>
        )}
      </CollapsibleSection>

      {/* Daily Staffing Levels - Full Featured */}
      <CollapsibleSection
        id="coverage"
        title="Daily Staffing Levels"
        icon={BarChart3}
        iconColor="bg-blue-100 text-blue-600"
        count={undefined}
      >
        <StaffingLevels />
      </CollapsibleSection>

      {/* Duplicate Shift Resolution Modal */}
      <DuplicateShiftModal
        duplicate={selectedDuplicate}
        open={!!selectedDuplicate}
        onClose={() => setSelectedDuplicate(null)}
        onResolved={() => {
          setSelectedDuplicate(null);
          // Clear sessionStorage cache so we get fresh data
          try { sessionStorage.removeItem(CACHE_KEY); } catch {}
          fetchInsights(true);
        }}
      />

      {/* AI Chat */}
      <AIChatBox
        initialMessage={chatMessage}
        onClear={() => setChatMessage(null)}
      />
    </div>
  );
};

export default ScheduleInsights;
